import type { DelegateBackoffFn, IExponentialBackoffOptions, IRetryBackoffContext, IFailureEvent } from 'cockatiel';
import { CancellationTokenSource, Policy, TaskCancelledError, TimeoutStrategy } from 'cockatiel';
import type { HttpError } from 'http-errors';
import isUndefined from 'lodash/isUndefined';
import get from 'lodash/get';

export type Logger = {
  debug: (...data: any[]) => void;
  warning: (...data: any[]) => void;
  info: (...data: any[]) => void;
};
let logger: Logger | undefined = undefined;
export const setLogger = (l: Logger) => {
  logger = l;
};

/** Only one of `cancelOnErrors` or `shouldRetryPredicate` can be provided — not both. */
type RetryErrorHandling =
  | { cancelOnErrors?: string[]; shouldRetryPredicate?: never }
  | { shouldRetryPredicate?: (error: Error) => boolean; cancelOnErrors?: never };

export type TimeoutAndRetryOptions = {
  attempts: number;
  timeoutMs: number;
} & RetryErrorHandling;

export type RetryOnErrorsOptions = {
  attempts: number;
  isRetryAllowed?: () => boolean;
};

export type DelegateBackoffOptions<T> = {
  delegateBackoffFn: DelegateBackoffFn<IRetryBackoffContext<never> & { result: { error?: T } }>;
  attempts: number;
  cancelOnErrors?: string[];
};

function onFailure(
  cancellationTokenSource: CancellationTokenSource,
  cancelOnErrors?: TimeoutAndRetryOptions['cancelOnErrors'],
  errorFieldName = 'message'
) {
  let attempt = 1;
  return ({ duration, handled, reason }: IFailureEvent) => {
    const error = 'error' in reason ? reason.error : undefined;
    logger?.warning(`RetryStrategies attempt failed`, { error, handled, duration, attempt: attempt++ });
    if (cancelOnErrors?.some(cancelOnErrorText => get(error, errorFieldName)?.includes?.(cancelOnErrorText))) {
      logger?.info('Cancelling retry and timeout');
      cancellationTokenSource.cancel();
    }
  };
}

type RetryPolicyOptions = {
  attempts?: number;
  fieldName?: string;
} & RetryErrorHandling;

function createRetryPolicy({ attempts, cancelOnErrors, fieldName, shouldRetryPredicate }: RetryPolicyOptions = {}) {
  const cancellationTokenSource = new CancellationTokenSource();

  const policy = shouldRetryPredicate ? Policy.handleWhen(shouldRetryPredicate) : Policy.handleAll();
  let retry = policy.retry();
  if (attempts) {
    retry = retry.attempts(attempts);
  }

  retry.onFailure(onFailure(cancellationTokenSource, cancelOnErrors, fieldName));

  return { retry, cancellationToken: cancellationTokenSource.token };
}

const withTimeoutAndRetry = <T>(fn: () => Promise<T>, { attempts, timeoutMs, ...rest }: TimeoutAndRetryOptions) => {
  const { retry, cancellationToken } = createRetryPolicy({ attempts, ...rest });
  const timeout = Policy.timeout(timeoutMs, TimeoutStrategy.Aggressive);

  return Policy.wrap(retry, timeout).execute(fn, cancellationToken);
};

type ExponentialBackoffOptions = Partial<IExponentialBackoffOptions<unknown>> & { retryPolicy?: RetryPolicyOptions };

const withExponentialBackoff = <T>(fn: () => Promise<T>, exponentialBackoffOptions: ExponentialBackoffOptions) => {
  const { retry, cancellationToken } = createRetryPolicy(exponentialBackoffOptions.retryPolicy);
  return retry.exponential(exponentialBackoffOptions).execute(fn, cancellationToken);
};

const withDelegateBackoff = <T, U = Error>(
  fn: () => Promise<T>,
  { delegateBackoffFn, attempts, cancelOnErrors }: DelegateBackoffOptions<U>
) => {
  const { retry, cancellationToken } = createRetryPolicy({ attempts, cancelOnErrors });
  return retry
    .delegate(delegateBackoffFn as DelegateBackoffFn<IRetryBackoffContext<unknown>, void>)
    .execute(fn, cancellationToken);
};
const withTimeout = <T>(fn: () => Promise<T>, timeoutMs: number, fnName?: string) => {
  const timeout = Policy.timeout(timeoutMs, TimeoutStrategy.Aggressive);

  if (fnName) {
    timeout.onTimeout(() => logger?.debug(`Timeout reached on ${fnName}`));
  }

  return timeout.execute(fn);
};

const shouldRetry = (err: Error): boolean => {
  const error = err as HttpError<number> | undefined;
  const status = error?.status ?? error?.response?.status; // In Axios error the status is in response.status

  // Check for DNS errors or other network-related issues
  const isNetworkError = Boolean(
    error?.message &&
    (error.message.includes('getaddrinfo EAI_AGAIN') ||
      error.message.includes('ENOTFOUND') ||
      error.message.includes('ECONNREFUSED') ||
      error.message.includes('ETIMEDOUT'))
  );

  return isUndefined(status) || status >= 500 || isNetworkError;
};

const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_FAILED_DEPENDENCY = 424;

export const shouldRetryIncludingRateLimits = (err: Error): boolean => {
  const status = (err as HttpError<number> | undefined)?.status;
  return status === HTTP_TOO_MANY_REQUESTS || shouldRetry(err);
};

const getErrorStatus = (err: unknown): number | undefined => {
  const status =
    get(err, 'status') ?? get(err, 'statusCode') ?? get(err, 'response.status') ?? get(err, 'response.statusCode');
  return typeof status === 'number' ? status : undefined;
};

export const shouldRetryRateLimitOrTransient = (err: Error): boolean => {
  const status = getErrorStatus(err);
  return isUndefined(status) || status === HTTP_TOO_MANY_REQUESTS || status >= 500;
};

export const shouldRetryIncludingFailedDependency = (err: Error): boolean =>
  getErrorStatus(err) === HTTP_FAILED_DEPENDENCY || shouldRetry(err);

const getHeaderValue = (headers: unknown, name: string): string | undefined => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }
  const record = headers as Record<string, unknown>;
  const value = record[name] ?? record[name.toLowerCase()];
  return isUndefined(value) ? undefined : String(value);
};

export const getRetryAfterMs = (error: unknown): number | undefined => {
  const rawRetryAfter =
    get(error, 'retryAfter') ??
    getHeaderValue(get(error, 'headers'), 'Retry-After') ??
    getHeaderValue(get(error, 'response.headers'), 'Retry-After') ??
    getHeaderValue(get(error, 'originalError.response.headers'), 'Retry-After');

  if (isUndefined(rawRetryAfter)) {
    return undefined;
  }

  const seconds = Number(rawRetryAfter);
  if (!Number.isNaN(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const dateMs = Date.parse(String(rawRetryAfter)) - Date.now();
  return Number.isNaN(dateMs) ? undefined : Math.max(0, dateMs);
};

export type RateLimitAwareBackoffOptions = {
  attempts: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  timeoutMs?: number;
};

const DEFAULT_RATE_LIMIT_INITIAL_DELAY_MS = 500;
const DEFAULT_RATE_LIMIT_MAX_DELAY_MS = 5000;

const jitteredExponentialDelayMs = (attempt: number, initialDelayMs: number, maxDelayMs: number): number => {
  const exponential = Math.min(maxDelayMs, initialDelayMs * 2 ** attempt);
  return Math.round(exponential * (0.5 + Math.random() * 0.5));
};

const withRateLimitAwareBackoff = <T>(
  fn: () => Promise<T>,
  {
    attempts,
    initialDelayMs = DEFAULT_RATE_LIMIT_INITIAL_DELAY_MS,
    maxDelayMs = DEFAULT_RATE_LIMIT_MAX_DELAY_MS,
    timeoutMs
  }: RateLimitAwareBackoffOptions
): Promise<T> => {
  const { retry, cancellationToken } = createRetryPolicy({
    attempts,
    shouldRetryPredicate: shouldRetryRateLimitOrTransient
  });

  const backoff = retry.delegate(({ attempt, result }) => {
    const error = (result as { error?: unknown })?.error;
    const retryAfterMs = getRetryAfterMs(error);
    if (!isUndefined(retryAfterMs)) {
      return Math.min(retryAfterMs, maxDelayMs);
    }
    return jitteredExponentialDelayMs(attempt, initialDelayMs, maxDelayMs);
  });

  const policy = isUndefined(timeoutMs)
    ? backoff
    : Policy.wrap(backoff, Policy.timeout(timeoutMs, TimeoutStrategy.Aggressive));

  return policy.execute(fn, cancellationToken);
};

const retryOnErrorsPolicy = <T>(fn: () => Promise<T>, { attempts, isRetryAllowed }: RetryOnErrorsOptions) => {
  const retryPolicy = Policy.handleWhen(err => shouldRetry(err) && (isRetryAllowed?.() ?? true))
    .retry()
    .attempts(attempts);

  retryPolicy.onRetry(data => {
    logger?.debug('retrying', data);
  });

  retryPolicy.onGiveUp(data => {
    logger?.warning('give up retrying', data);
  });

  return retryPolicy;
};

const withRetryOnErrors = <T>(fn: () => Promise<T>, options: RetryOnErrorsOptions) => {
  const retryPolicy = retryOnErrorsPolicy(fn, options);
  return retryPolicy.execute(fn);
};

const withRetryOnErrorsAndExponentialBackoff = <T>(
  fn: () => Promise<T>,
  {
    maxAttempts,
    ...exponentialBackoffOptions
  }: Partial<Omit<IExponentialBackoffOptions<unknown>, 'maxAttempts'>> & { maxAttempts: number }
) => {
  const retryPolicy = retryOnErrorsPolicy(fn, { attempts: maxAttempts });
  retryPolicy.exponential(exponentialBackoffOptions);
  return retryPolicy.execute(fn);
};

export const retryStrategies = {
  withTimeoutAndRetry,
  withExponentialBackoff,
  withDelegateBackoff,
  withTimeout,
  withRetryOnErrors,
  withRetryOnErrorsAndExponentialBackoff,
  withRateLimitAwareBackoff,
  TimeoutError: TaskCancelledError
};
